import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import CallTasksPanel, { LOGGED_EXIT_MS } from './CallTasksPanel';
import CopyPanel, { CopyTabId, tabsFor } from './CopyPanel';
import LogCallView from './LogCallView';
import { stepChannel } from './overviewModel';
import { useLifecycleToast } from './Toast';
import { C, FONT, Icon, PATHS, Plain, FilledButton, text14 } from './ui';
import { useLifecycle } from '../LifecycleContext';
import { outcomeLabel } from '../shared/labels';
import useCopyMessage from '../CopyTab/useCopyMessage';

import ConfirmationModal from '@/components/ConfirmationModalV2';
import useAuth from '@/hooks/useAuth';
import {
  CallTask,
  CallTaskOutcome,
  JourneyStep,
  lifecycleAPI,
  lifecycleErrorMessage,
  LifecycleJourney,
} from '@/services/lifecycleAPI';
import { trackLifecycleFeatureUsed } from '@/util/vexo';

type TabId = CopyTabId | 'call';

const TAB_LABELS: Record<TabId, string> = {
  sms: 'SMS',
  push: 'Push notification',
  email: 'Email',
  call: 'Call tasks',
};

// The open call tasks of one journey, for the Call tasks tab and its badge.
const useJourneyCallTasks = (journeyKey: string, enabled: boolean) => {
  const [tasks, setTasks] = useState<CallTask[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const [loads, setLoads] = useState(0);

  useEffect(() => {
    if (!enabled) return undefined;
    let active = true;
    setLoading(true);
    setError(null);
    lifecycleAPI
      .listCallTasks({ status: 'open', journey_key: journeyKey, per_page: 100 })
      .then((res) => {
        if (!active) return;
        setTasks(res.data);
        setTotal(res.pagination.total);
      })
      .catch((err) => {
        if (active)
          setError(
            lifecycleErrorMessage(err, 'Could not load the call tasks.'),
          );
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [journeyKey, enabled, loads]);

  const reload = useCallback(() => setLoads((n) => n + 1), []);

  return { tasks, total, loading, error, reload };
};

type Props = {
  journey: LifecycleJourney;
  step: JourneyStep;
  onClose: (changed: boolean) => void;
};

const StepModal = ({ journey, step, onClose }: Props) => {
  const { currentAdminIsTenantAdminRole: canEdit } = useAuth();
  const { setCopyDirty, refreshOpenCount } = useLifecycle();
  const { toast } = useLifecycleToast();
  const isCall = stepChannel(step) === 'call';
  const hasCallTab =
    isCall || journey.definition.steps.some((s) => s.kind === 'call_task');
  // A call step has no message copy: the modal opens straight on its calls.
  const message = useCopyMessage(isCall ? null : step.key, 'en', 'step_modal');
  const calls = useJourneyCallTasks(journey.journey_key, hasCallTab);
  const opening = stepChannel(step);
  const [tab, setTab] = useState<TabId>(opening === 'call' ? 'call' : opening);
  const [logging, setLogging] = useState<CallTask | null>(null);
  const [logged, setLogged] = useState<number[]>([]);
  const [gone, setGone] = useState<number[]>([]);
  const exitTimers = useRef<number[]>([]);
  const [leaving, setLeaving] = useState(false);
  const [logDirty, setLogDirty] = useState(false);
  const [logLeave, setLogLeave] = useState<'back' | 'close' | null>(null);
  const changed = useRef(false);
  // The open call changed on the server (a save, or Reload after a 409): the
  // list reloads when the admin goes back to it.
  const callListStale = useRef(false);
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setCopyDirty(message.dirty);
  }, [message.dirty, setCopyDirty]);
  useEffect(() => () => setCopyDirty(false), [setCopyDirty]);
  useEffect(() => () => exitTimers.current.forEach(clearTimeout), []);
  useEffect(() => {
    setLogged([]);
    setGone([]);
  }, [calls.tasks]);

  const { reload: reloadCalls } = calls;
  // Not after a failed reload: the check turns true again when it ends, and
  // would ask again and again. The panel offers Try again instead.
  const lastLoggableGone =
    !calls.loading &&
    !calls.error &&
    logged.length > 0 &&
    calls.total > calls.tasks.length &&
    calls.tasks.every(
      (task) => gone.includes(task.id) || Boolean(task.do_not_call_reason),
    );
  useEffect(() => {
    if (lastLoggableGone) reloadCalls();
  }, [lastLoggableGone, reloadCalls]);

  const copyTabs = isCall ? [] : tabsFor(message);
  const shownCopyTabs: CopyTabId[] =
    copyTabs.length > 0 || isCall ? copyTabs : [opening as CopyTabId];
  const tabs: TabId[] = [
    ...shownCopyTabs,
    ...(hasCallTab ? ['call' as const] : []),
  ];
  const current: TabId = tabs.includes(tab) ? tab : tabs[0];

  const finish = useCallback(() => onClose(changed.current), [onClose]);

  const close = useCallback(() => {
    if (logging && logDirty) setLogLeave('close');
    else if (message.dirty) setLeaving(true);
    else finish();
  }, [logging, logDirty, message.dirty, finish]);

  const leaveCall = useCallback(() => {
    setLogging(null);
    if (!callListStale.current) return;
    callListStale.current = false;
    reloadCalls();
  }, [reloadCalls]);

  const backToCalls = useCallback(() => {
    if (logDirty) {
      setLogLeave('back');
      return;
    }
    leaveCall();
  }, [logDirty, leaveCall]);

  const callChanged = useCallback(() => {
    callListStale.current = true;
    refreshOpenCount();
  }, [refreshOpenCount]);

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    box.current?.focus();
    return () => {
      if (opener?.isConnected) opener.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (
        event.key !== 'Escape' ||
        event.defaultPrevented ||
        leaving ||
        logLeave
      )
        return;
      if (document.querySelector('[data-testid="modal"]')) return;
      if (logging && logDirty) return;
      close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [close, leaving, logLeave, logging, logDirty]);

  const openCount = Math.max(calls.total - logged.length, 0);
  const title = logging
    ? `Call ${logging.worker.name}`
    : `${journey.name} message copy`;

  const onLogged = (task: CallTask, outcome: CallTaskOutcome) => {
    callListStale.current = false;
    setLogged((list) => [...list, task.id]);
    exitTimers.current.push(
      window.setTimeout(
        () => setGone((list) => [...list, task.id]),
        LOGGED_EXIT_MS,
      ),
    );
    setLogging(null);
    setLogDirty(false);
    toast(
      'Call logged',
      `Call with ${task.worker.name} logged as "${outcomeLabel(outcome)}".`,
    );
  };

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9000,
        fontFamily: FONT,
        color: C.black,
      }}
    >
      <div
        aria-hidden="true"
        data-testid="stepModalOverlay"
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.7)',
        }}
        onClick={close}
      />
      <div
        aria-label={title}
        aria-modal="true"
        data-testid="stepModal"
        ref={box}
        role="dialog"
        tabIndex={-1}
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -50%)',
          width: 640,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          borderRadius: 12,
          background: 'rgb(254,254,254)',
          boxShadow: `inset 0 0 0 1px ${C.stroke}`,
          display: 'flex',
          flexDirection: 'column',
          gap: 20,
          outline: 'none',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 16px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {logging && (
              <Plain
                aria-label="Back to call tasks"
                style={{ width: 20, height: 20, display: 'flex' }}
                title="Back to call tasks"
                type="button"
                onClick={backToCalls}
              >
                <Icon d={PATHS.back} size={20} />
              </Plain>
            )}
            <span style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.4 }}>
              {title}
            </span>
          </div>
          <Plain
            aria-label="Close"
            data-testid="stepModalClose"
            style={{
              width: 24,
              height: 24,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            type="button"
            onClick={close}
          >
            <Icon d={PATHS.close} size={20} />
          </Plain>
        </div>

        {logging ? (
          <LogCallView
            journeyKey={journey.journey_key}
            task={logging}
            onBack={backToCalls}
            onChanged={callChanged}
            onDirty={setLogDirty}
            onLogged={onLogged}
          />
        ) : (
          <>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 20,
                padding: current === 'call' ? '0 24px 60px' : '0 24px',
              }}
            >
              <div
                role="tablist"
                style={{
                  display: 'flex',
                  gap: 16,
                  borderBottom: `1px solid ${C.stroke}`,
                }}
              >
                {tabs.map((id) => {
                  const on = id === current;
                  return (
                    <Plain
                      aria-selected={on}
                      data-testid={`stepModalTab-${id}`}
                      key={id}
                      role="tab"
                      style={{
                        padding: '0 8px 8px',
                        fontSize: 16,
                        lineHeight: 1.4,
                        marginBottom: -1,
                        fontWeight: on ? 700 : 400,
                        borderBottom: `2px solid ${on ? C.greenFill : 'transparent'}`,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        whiteSpace: 'nowrap',
                      }}
                      type="button"
                      onClick={() => {
                        if (id !== tab) {
                          trackLifecycleFeatureUsed({
                            feature: 'step_tab',
                            surface: 'step_modal',
                            journey_key: journey.journey_key,
                            step_key: step.key,
                            channel: id,
                          });
                        }
                        setTab(id);
                      }}
                    >
                      <span>{TAB_LABELS[id]}</span>
                      {id === 'call' && openCount > 0 && (
                        <span
                          data-testid="callTasksBadge"
                          style={{
                            width: 20,
                            height: 20,
                            borderRadius: 100,
                            background: C.badgeGreen,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontWeight: 600,
                            fontSize: 12,
                            color: C.green,
                          }}
                        >
                          {openCount}
                        </span>
                      )}
                    </Plain>
                  );
                })}
              </div>
              {current === 'call' ? (
                <CallTasksPanel
                  error={calls.error}
                  loading={calls.loading}
                  onRetry={reloadCalls}
                  gone={gone}
                  logged={logged}
                  openCount={openCount}
                  tasks={calls.tasks}
                  onLog={(task) => {
                    trackLifecycleFeatureUsed({
                      feature: 'call_task_open',
                      surface: 'step_modal',
                      journey_key: journey.journey_key,
                      step_key: task.step_key,
                      task_id: task.id,
                    });
                    setLogging(task);
                  }}
                />
              ) : message.loading ? (
                <div style={{ ...text14, color: C.grey, padding: '24px 0' }}>
                  Loading the message...
                </div>
              ) : message.error || message.notFound ? (
                <div
                  role="alert"
                  style={{ ...text14, color: C.red, padding: '24px 0' }}
                >
                  {message.error ?? 'This message has no copy to edit.'}
                </div>
              ) : (
                <CopyPanel
                  canEdit={canEdit}
                  journeyName={journey.name}
                  message={message}
                  stepKey={step.key}
                  tab={current}
                  onWritten={() => {
                    changed.current = true;
                  }}
                />
              )}
            </div>
            {current === 'call' && (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  padding: 16,
                }}
              >
                <FilledButton type="button" onClick={close}>
                  Go back
                </FilledButton>
              </div>
            )}
          </>
        )}
      </div>
      {logLeave && (
        <ConfirmationModal
          disableClickout
          cancelLabel="Keep editing"
          confirmLabel="Discard"
          message="Your call notes and choices are not saved."
          subMessage={null}
          title="Discard unsaved changes?"
          onCancel={() => setLogLeave(null)}
          onConfirm={() => {
            const next = logLeave;
            setLogLeave(null);
            setLogDirty(false);
            leaveCall();
            if (next !== 'close') return;
            // Copy edits on another tab still get their own question.
            if (message.dirty) setLeaving(true);
            else finish();
          }}
        />
      )}
      {leaving && (
        <ConfirmationModal
          disableClickout
          cancelLabel="Keep editing"
          confirmLabel="Discard"
          message="Your edits to this message are not saved."
          subMessage={null}
          title="Discard unsaved changes?"
          onCancel={() => setLeaving(false)}
          onConfirm={() => {
            setLeaving(false);
            finish();
          }}
        />
      )}
    </div>,
    document.body,
  );
};

export default StepModal;
